<template>
  <v-dialog v-model="dialog" max-width="600">
    <v-card flat color="grey-lighten-4">
      <v-card-text class="pa-0">
        <v-row align="center" justify="center" no-gutters>
          <!-- Close Button Row -->
          <v-col cols="12" :class="{ 'd-none': isZooming }" class="pa-4">
            <v-row align="center">
              <v-spacer />
              <v-btn
                class="pa-2"
                variant="outlined"
                icon
                color="red"
                @click="close"
              >
                <v-icon large> mdi-close </v-icon>
              </v-btn>
            </v-row>
          </v-col>

          <!-- Image -->
          <v-col cols="12">
            <v-img
              v-if="dialog"
              class="bg-white"
              :style="{ transform: `rotate(${rotation}deg)` }"
              :src="sourceNew"
              aspect-ratio="1"
              contain
              @mousemove="zoomIn"
              @mouseleave="zoomOut"
            >
              <template #placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey-lighten-5" />
                </v-row>
              </template>
            </v-img>
          </v-col>

          <!-- Rotate + Toggle Buttons -->
          <v-col cols="12" :class="{ 'd-none': isZooming }" class="mt-5">
            <v-row align="center" justify="center" class="flex-wrap ga-2 px-2">
              <v-btn icon variant="text" @click="rotateL">
                <v-icon>mdi-rotate-left</v-icon>
              </v-btn>
              <v-btn icon variant="text" @click="rotateR">
                <v-icon>mdi-rotate-right</v-icon>
              </v-btn>
              <v-btn
                v-if="
                  $store.getters.institutionDisplayFeatures
                    .removeImagesBackground
                "
                variant="outlined"
                color="primary"
                @click="removeBackground = !removeBackground"
              >
                <v-icon start>
                  {{
                    removeBackground
                      ? "mdi-checkbox-marked-outline"
                      : "mdi-checkbox-blank-outline"
                  }}
                </v-icon>
                {{ $t("remove_background") }}
              </v-btn>
              <v-btn
                v-if="canRestore"
                variant="outlined"
                color="orange"
                @click="restoreOriginal"
              >
                <v-icon start>mdi-backup-restore</v-icon>
                {{ $t("restore_original") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions :class="{ 'd-none': isZooming }" class="mt-1">
        <v-btn variant="text" color="red-darken-2" @click="close">
          {{ $t("close") }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="edited"
          :loading="loading"
          @click="edit"
        >
          {{ $t("preview") }}
        </v-btn>
        <v-btn
          variant="outlined"
          color="success"
          :disabled="!edited"
          @click="save"
        >
          {{ $t("save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import zoom from "../utils/zoom";

const CDN_BASE = "https://leko-tech.fra1.cdn.digitaloceanspaces.com";

function dataURLtoBlob(dataUrl) {
  // convert base64 to raw binary data held in a string
  const byteString = atob(dataUrl.split(",")[1]);
  // separate out the mime component
  const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  // create a view into the buffer
  const ia = new Uint8Array(ab);
  // set the bytes of the string to the correct values
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  // write the ArrayBuffer to a blob, and you're done
  return new Blob([ab], { type: mimeString });
}

export default {
  data: () => ({
    dialog: false,
    source: "", // must be a dataurl
    details: { type: null, index: null },
    isZooming: false,
    rotation: 0,
    removeBackground: false,
    edited: false,
    loading: false,
    sourceNew: "",
    openedImageKey: null,
    openedThumb: null,
    openedDataUrl: null,
    persistedOriginalUrl: null,
    persistedOriginalThumb: null,
    persistedOriginalDataUrl: null,
    persistedOriginalBlob: null,
    sessionOriginalUrl: null,
    sessionOriginalThumb: null,
    sessionOriginalDataUrl: null,
    sessionOriginalBlob: null,
    sessionRemovedBackground: false,
    restoredOriginal: false,
  }),
  computed: {
    canRestore() {
      if (this.restoredOriginal || this.loading) return false;
      return !!(
        this.persistedOriginalUrl ||
        this.persistedOriginalDataUrl ||
        this.sessionOriginalUrl ||
        this.sessionOriginalDataUrl
      );
    },
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.close();
      }
    },
  },
  methods: {
    open(image) {
      this.resetOriginalState();
      this.dialog = true;
      this.details = { type: image.type, index: image.index };
      const { data } = image;

      if (image.type === "dataUrl") {
        const src = typeof data === "string" ? data : data.dataUrl;
        this.source = src;
        this.sourceNew = src;
        this.openedDataUrl = src;
        if (data && typeof data === "object") {
          this.persistedOriginalDataUrl = data.originalDataUrl || null;
          this.persistedOriginalBlob = data.originalBlob || null;
        }
        return;
      }

      this.sourceNew = `${CDN_BASE}/${data.image}`;
      this.source = `https://api.lekotech.com/images?q=${data.image}`;
      this.openedImageKey = data.image || null;
      this.openedThumb = data.thumb || null;
      this.persistedOriginalUrl = data.originalUrl || null;
      this.persistedOriginalThumb = data.originalThumb || null;
    },
    rotateR() {
      this.rotation += 90;
    },
    rotateL() {
      this.rotation -= 90;
    },
    zoomIn(event) {
      this.isZooming = true;
      zoom.zoomIn(2.4, event, this.rotation);
    },
    zoomOut(event) {
      this.isZooming = false;
      zoom.zoomOut(event, this.rotation);
    },
    captureSessionOriginal() {
      if (
        this.persistedOriginalUrl ||
        this.persistedOriginalDataUrl ||
        this.sessionOriginalUrl ||
        this.sessionOriginalDataUrl
      ) {
        return;
      }
      if (this.openedImageKey) {
        this.sessionOriginalUrl = this.openedImageKey;
        this.sessionOriginalThumb = this.openedThumb;
        return;
      }
      if (this.openedDataUrl) {
        this.sessionOriginalDataUrl = this.openedDataUrl;
        this.sessionOriginalBlob = dataURLtoBlob(this.openedDataUrl);
      }
    },
    restoreOriginal() {
      const dataUrl =
        this.persistedOriginalDataUrl || this.sessionOriginalDataUrl;
      const key = this.persistedOriginalUrl || this.sessionOriginalUrl;
      if (dataUrl) {
        this.source = dataUrl;
        this.sourceNew = dataUrl;
      } else if (key) {
        this.sourceNew = `${CDN_BASE}/${key}`;
        this.source = `https://api.lekotech.com/images?q=${key}`;
      } else {
        return;
      }
      this.rotation = 0;
      this.removeBackground = false;
      this.restoredOriginal = true;
      // Persist restore only when the displayed image was already a saved bg-removed version.
      this.edited = !!this.persistedOriginalUrl || !!this.persistedOriginalDataUrl;
    },

    async edit() {
      this.loading = true;
      // Set the oprations.
      const operations = [];
      const removingBackground = this.removeBackground;
      if (removingBackground) operations.push("removeBackground");
      if (this.rotation !== 0) operations.push("rotate");
      this.restoredOriginal = false;
      // Send the edit request.
      const imageResponse = await fetch(this.source);
      const blob = await imageResponse.blob();
      const formData = new FormData();
      formData.append("image", blob);
      axios
        .post(
          `${this.$serverUri}/images/edit?operations=${operations.join(",")}&rotate=${this.rotation}`,
          formData,
          {
            headers: {
              authorization: localStorage.token,
              "Content-Type": "multipart/form-data",
            },
            responseType: "blob",
          },
        )
        .then((response) => {
          const reader = new window.FileReader();
          reader.readAsDataURL(response.data);
          reader.onload = () => {
            this.source = reader.result;
            this.sourceNew = reader.result;
            if (removingBackground) {
              this.captureSessionOriginal();
              this.sessionRemovedBackground = true;
            }
          };
          this.rotation = 0;
          this.removeBackground = false;
          this.edited = true;
        })
        .catch(() => {
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    save() {
      if (this.restoredOriginal) {
        const shouldPersistRestore =
          (this.persistedOriginalUrl || this.persistedOriginalDataUrl) &&
          !this.sessionRemovedBackground;
        if (shouldPersistRestore) {
          this.$emit("save", {
            type: this.details.type,
            index: this.details.index,
            restoreOriginal: true,
            originalUrl: this.persistedOriginalUrl,
            originalThumb: this.persistedOriginalThumb,
            originalDataUrl: this.persistedOriginalDataUrl,
            originalBlob: this.persistedOriginalBlob,
          });
        }
        this.close();
        return;
      }

      const originalUrl =
        this.persistedOriginalUrl ||
        (this.sessionRemovedBackground ? this.sessionOriginalUrl : null);
      const originalThumb =
        this.persistedOriginalThumb ||
        (this.sessionRemovedBackground ? this.sessionOriginalThumb : null);
      const originalDataUrl =
        this.persistedOriginalDataUrl ||
        (this.sessionRemovedBackground ? this.sessionOriginalDataUrl : null);
      const originalBlob =
        this.persistedOriginalBlob ||
        (this.sessionRemovedBackground ? this.sessionOriginalBlob : null);

      this.$emit("save", {
        type: this.details.type,
        index: this.details.index,
        dataUrl: this.source,
        blob: dataURLtoBlob(this.source),
        title: `${Math.random().toString(36).substring(2)}.${this.source.split(";")[0].split("/")[1]}`,
        originalUrl,
        originalThumb,
        originalDataUrl,
        originalBlob,
      });
      this.close();
    },
    resetOriginalState() {
      this.openedImageKey = null;
      this.openedThumb = null;
      this.openedDataUrl = null;
      this.persistedOriginalUrl = null;
      this.persistedOriginalThumb = null;
      this.persistedOriginalDataUrl = null;
      this.persistedOriginalBlob = null;
      this.sessionOriginalUrl = null;
      this.sessionOriginalThumb = null;
      this.sessionOriginalDataUrl = null;
      this.sessionOriginalBlob = null;
      this.sessionRemovedBackground = false;
      this.restoredOriginal = false;
    },
    close() {
      this.source = "";
      this.sourceNew = "";
      this.details = { type: null, index: null };
      this.dialog = false;
      this.isZooming = false;
      this.rotation = 0;
      this.rotation = 0;
      this.removeBackground = false;
      this.edited = false;
      this.loading = false;
      this.resetOriginalState();
      this.$emit("close");
    },
  },
};
</script>
